<template>
  <div>
    <asc-dialog
      text="Add Users"
      :width="1200"
      :show-dialog="showDialog"
      primary-button-text="Submit"
      :on-primary="onSubmitDialog"
      :primary-available="rowCount > 0 && rowsValid && areFieldsUnique"
      :on-close="onCloseDialog"
      :loading="submitLoading"
    >
      <v-container>
        <v-row>
          <v-col>
            <p>
              Add a user manually or import from an excel file to get started.
            </p>
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col cols="3">
            <asc-file-upload-input
              v-model="files"
              :on-upload-file="importFile"
              :accepted-file-properties="acceptedFileProperties"
              :disabled="rowCount === maxRows"
            />
          </v-col>
          <v-col class="d-flex align-self-center">
            <asc-accent-button
              button-text="Add User(s)"
              :on-click="addManualUserClicked"
              secondary
              :disabled="rowCount === maxRows"
              :block="false"
              class="ml-2"
            />
            <asc-accent-button
              v-if="rowCount > 0"
              button-text="Edit"
              :on-click="editUserClicked"
              secondary
              :disabled="selections.length === 0"
              class="ml-2"
            />
            <asc-accent-button
              v-if="rowCount > 0"
              button-text="Duplicate User"
              :on-click="duplicateUserClicked"
              secondary
              :disabled="selections.length !== 1 || rowCount === maxRows"
              class="ml-2"
            />
            <asc-accent-button
              v-if="rowCount > 0"
              button-text="Delete User(s)"
              :on-click="deleteUserClicked"
              secondary
              :disabled="selections.length === 0"
              class="ml-2"
            />
          </v-col>
        </v-row>
        <v-row no-gutters>
          <v-col>
            <asc-validation-message
              message="You have invalid rows. All columns are required to submit."
              :show="!rowsValid && rowCount > 0"
            />
            <asc-validation-message
              message="You have duplicate username or email fields. These fields must be unique for each user."
              :show="rowCount > 1 && !areFieldsUnique"
            />
          </v-col>
        </v-row>
        <v-row no-gutters class="asc-bulk-user-mgmt-grid">
          <v-col>
            <asc-ag-grid-filter
              :class="getGridClasses"
              :grid-options="agGridOptions"
              :column-defs="getGridColumnDefs"
              :row-data="agGridRowData"
              grid-size="sm"
              @onSelectionChanged="handleSelectionChanged"
            />
          </v-col>
        </v-row>
      </v-container>
    </asc-dialog>

    <asc-dialog
      :text="getSmallDialogTitle"
      :width="1000"
      :show-dialog="showSmallDialog"
      primary-button-text="Submit"
      :on-primary="submitSmallDialogClicked"
      :on-close="onCloseSmallDialog"
    >
      <v-container class="asc-bulk-user-mgmt-container mb-2">
        <v-row v-if="addingUser">
          <v-col cols="3">
            <asc-autocomplete-selector
              v-model="usersToAdd"
              label="Number of users to add"
              :items="rowsToAddList"
              dense
              short
              fixed-position
              no-array
              :sort-items="false"
              @input="usersToAddChanged(...arguments)"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="3">
            <asc-text-field-input
              v-model="currentUser.firstName"
              label="First Name"
              required
              :disabled="
                (addingUser && usersToAdd > 1) ||
                (editingUser && selections.length > 1)
              "
            />
          </v-col>
          <v-col cols="3">
            <asc-text-field-input
              v-model="currentUser.lastName"
              label="Last Name"
              required
              :disabled="
                (addingUser && usersToAdd > 1) ||
                (editingUser && selections.length > 1)
              "
            />
          </v-col>
          <v-col cols="3">
            <asc-text-field-input
              v-model="currentUser.signInName"
              label="Username"
              required
              placeholder="username@domain.com"
              :disabled="
                (addingUser && usersToAdd > 1) ||
                (editingUser && selections.length > 1)
              "
            />
          </v-col>
          <v-col cols="3">
            <asc-text-field-input
              v-model="currentUser.userEmail"
              label="Email"
              required
              :disabled="
                (addingUser && usersToAdd > 1) ||
                (editingUser && selections.length > 1)
              "
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col v-if="!addNewCompany" cols="3">
            <asc-autocomplete-selector
              v-model="company"
              label="Company"
              :items="companyOptions"
              dense
              short
              required
              fixed-position
              use-strings
              no-array
            />
          </v-col>
          <v-col v-if="addNewCompany" cols="3">
            <div>
              <asc-text-field-input
                v-model="company"
                label="Company"
                placeholder="Add New Company"
                required
              />
              <asc-tooltip top tooltip="Revert to Company List">
                <v-btn text @click="revertToDropdown">
                  <asc-close-icon></asc-close-icon>
                </v-btn>
              </asc-tooltip>
            </div>
          </v-col>
          <v-col cols="3">
            <asc-autocomplete-selector
              v-model="currentUser.country"
              label="Country"
              :items="countries"
              dense
              short
              required
              fixed-position
              use-strings
              no-array
            />
          </v-col>
          <v-col cols="1">
            <asc-switch
              v-model="currentUser.isActive"
              label="Active"
              disabled
            />
          </v-col>
          <v-col cols="1">
            <asc-switch v-model="currentUser.isUSPerson" label="US Person" />
          </v-col>
          <v-col cols="1">
            <asc-switch
              v-model="currentUser.isCollinsInternal"
              label="Internal"
            />
          </v-col>
          <v-col cols="1">
            <asc-switch v-model="currentUser.isFederated" label="Federated" />
          </v-col>
          <v-col cols="1">
            <asc-switch v-model="currentUser.sendInvite" label="Invitation" />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="3">
            <asc-autocomplete-selector
              v-model="currentUser.airlineIds"
              label="Airlines"
              :items="airlinesOptions"
              dense
              short
              required
              fixed-position
              component-type="multiple"
            />
          </v-col>
          <v-col cols="3">
            <asc-autocomplete-selector
              v-model="currentUser.roleIds"
              label="Legacy Roles"
              :items="roleOptions"
              dense
              short
              required
              fixed-position
              component-type="multiple"
            />
          </v-col>
          <v-col cols="3">
            <asc-autocomplete-selector
              v-model="currentUser.groupIds"
              label="Roles"
              :items="groupOptions"
              dense
              short
              required
              fixed-position
              component-type="multiple"
            />
          </v-col>
          <v-col cols="3">
            <asc-autocomplete-selector
              v-model="currentUser.acmfReportTypeIds"
              label="Report Types"
              :items="reportsOptions"
              dense
              short
              required
              fixed-position
              component-type="multiple"
            />
          </v-col>
        </v-row>
      </v-container>
    </asc-dialog>
  </div>
</template>

<script>
import AscDialog from '@/components/atoms/AscDialog'
import AscTextFieldInput from '@/components/atoms/AscTextFieldInput'
import AscAutocompleteSelector from './AscAutocompleteSelector'
import AscCloseIcon from '@/components/atoms/AscCloseIcon'
import { AscAgGridFilter, AscTooltipCellRenderer } from '@/components/agGrid'
import { AscGridPropsMixin, AscGridMethodsMixin } from '@/components/mixins'
import AscAccentButton from '@/components/atoms/AscAccentButton'
import { v4 as uuidv4 } from 'uuid'
import { read, utils } from 'xlsx'
import { COUNTRIES } from '@/utilities/appConsts'
import AscSwitch from '@/components/atoms/AscSwitch'
import AscValidationMessage from '@/components/atoms/AscValidationMessage'
import AscFileUploadInput from '@/components/molecules/AscFileUploadInput'
import AscTooltip from '@/components/atoms/AscTooltip'
import { getFilterOptions } from '@/components/agGrid/AgGridOptions'

export default {
  name: 'AscBulkUserMgmtDialog',

  components: {
    AscDialog,
    AscTextFieldInput,
    AscAgGridFilter,
    AscAutocompleteSelector,
    AscAccentButton,
    AscSwitch,
    AscValidationMessage,
    AscFileUploadInput,
    AscCloseIcon,
    AscTooltip,
    AscTooltipCellRenderer,
  },

  mixins: [AscGridPropsMixin, AscGridMethodsMixin],

  props: {
    showDialog: {
      type: Boolean,
      default: false,
    },
    airlines: {
      type: Array,
      default: () => [],
    },
    reportTypes: {
      type: Array,
      default: () => [],
    },
    roles: {
      type: Array,
      default: () => [],
    },
    groups: {
      type: Array,
      default: () => [],
    },
    companies: {
      type: Array,
      default: () => [],
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    maxRows: {
      type: Number,
      default: 20,
    },
    submitLoading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      valid: false,
      rowsValid: false,
      showSmallDialog: false,
      editingUser: false,
      duplicateUser: false,
      addingUser: false,
      selections: [],
      usersToAdd: 1,
      rowCount: 0,
      addNewCompany: false,
      files: [],
      newCompanies: [],
      acceptedFileProperties: {
        name: 'attachments',
        filenameSuffix: 'attachment',
        description: 'Attachments',
        allowedFileTypes: '.xlsx, .xls',
        maxFileSizeInMB: 10,
      },
      currentUser: {
        firstName: '',
        lastName: '',
        signInName: '',
        userEmail: '',
        company: null,
        country: '',
        isUSPerson: false,
        isActive: true,
        isCollinsInternal: false,
        isFederated: false,
        sendInvite: false,
        airlineIds: [],
        acmfReportTypeIds: [],
        roleIds: [],
        groupIds: [],
        id: null,
      },
      selected: [],
      rowValues: [],
      agGridOptions: getFilterOptions(this, {
        defaultColDef: {
          floatingFilter: false,
          tooltipComponent: 'AscTooltipCellRenderer',
        },
        rowSelection: 'multiple',
        getRowId: params => {
          return params.data && params.data.id ? params.data.id : undefined
        },
        localeText: {
          noRowsToShow: 'No Users Added',
        },
        onGridReady: this.handleGridReady,
        onFirstDataRendered: this.handleFirstDataRendered,
        onGridSizeChanged: this.handleGridSizeChanged,
      }),
    }
  },

  computed: {
    company: {
      get() {
        return this.currentUser.company
      },
      set(val) {
        if (this.addNewCompany) {
          this.currentUser.company = val
        } else {
          const isInCompanyList = this.companies.filter(
            item => item.name === val,
          )
          if (
            val &&
            isInCompanyList.length === 0 &&
            !this.newCompanies.includes(val)
          ) {
            this.addNewCompany = true
            this.currentUser.company = ''
          } else {
            this.currentUser.company = val
          }
        }
      },
    },

    getRowCount() {
      let rowCount = 0
      this.agGridOptions.api.forEachNode(() => {
        rowCount++
      })
      return rowCount
    },

    areFieldsUnique() {
      const usernames = []
      const emails = []
      this.agGridOptions.api.forEachNode(rowNode => {
        if (
          rowNode.data.signInName === '' ||
          !usernames.includes(rowNode.data.signInName)
        ) {
          usernames.push(rowNode.data.signInName)
        }
        if (
          rowNode.data.userEmail === '' ||
          !emails.includes(rowNode.data.userEmail)
        ) {
          emails.push(rowNode.data.userEmail)
        }
      })
      return (
        usernames.length === this.rowCount && emails.length === this.rowCount
      )
    },

    validateRows() {
      const invalidRows = []
      this.agGridOptions.api.forEachNode(rowNode => {
        for (const item in rowNode.data) {
          if (
            item === 'isActive' ||
            item === 'isCollinsInternal' ||
            item === 'isFederated' ||
            item === 'isUSPerson' ||
            item === 'sendInvite'
          ) {
            continue
          } else if (!(rowNode.data[item] && rowNode.data[item].length > 0)) {
            invalidRows.push(rowNode)
            break
          }
        }
      })
      return invalidRows.length === 0
    },

    roleOptions() {
      if (this.roles) {
        return this.roles.map(item => ({
          text: item.name,
          value: item.id,
        }))
      }
      return []
    },

    groupOptions() {
      if (this.groups) {
        return this.groups.map(item => ({
          text: item.name,
          value: item.id,
        }))
      }
      return []
    },

    airlinesOptions() {
      if (this.airlines) {
        return this.airlines.map(item => ({
          text: item.name,
          value: item.id,
        }))
      }
      return []
    },

    companyOptions() {
      if (this.companies) {
        const companyList = this.companies.map(item => ({
          text: item.name,
          value: item.id,
        }))
        companyList.push({
          text: '---Add New---',
          value: uuidv4(),
        })
        if (this.newCompanies.length) {
          this.newCompanies.forEach(item => {
            companyList.push({
              text: item,
              value: uuidv4(),
            })
          })
        }
        return companyList
      }
      return []
    },

    reportsOptions() {
      if (this.reportTypes) {
        return this.reportTypes.map(item => ({
          text: item.displayName,
          value: item.id,
        }))
      }
      return []
    },

    getSmallDialogTitle() {
      return this.addingUser
        ? 'Add Users'
        : this.editingUser
        ? 'Edit Users'
        : 'Duplicate User'
    },

    countries() {
      if (COUNTRIES) {
        return COUNTRIES.map(item => ({
          text: item,
          value: item,
        }))
      }
      return []
    },

    rowsToAddList() {
      const countList = []
      for (let x = 1; x + this.rowCount <= this.maxRows; x++) {
        countList.push({
          text: x.toString(),
          value: x,
        })
      }
      return countList
    },

    agGridRowData() {
      this.resetLocalStorage(this.localStorageName)
      return this.rowValues
    },

    getGridColumnDefs() {
      return [
        {
          width: 42,
          maxWidth: 42,
          resizable: false,
          checkboxSelection: true,
          headerCheckboxSelection: true,
          suppressMovable: true,
          suppressSizeToFit: true,
          field: '',
        },
        {
          minWidth: 130,
          headerName: 'First Name',
          field: 'firstName',
        },
        {
          minWidth: 130,
          headerName: 'Last Name',
          field: 'lastName',
        },
        {
          minWidth: 130,
          headerName: 'Username',
          field: 'signInName',
        },
        {
          minWidth: 130,
          headerName: 'Email',
          field: 'userEmail',
        },
        {
          minWidth: 130,
          headerName: 'Company',
          field: 'company',
        },
        {
          minWidth: 130,
          headerName: 'Country',
          field: 'country',
        },
        {
          minWidth: 130,
          headerName: 'US Person',
          field: 'isUSPerson',
        },
        {
          minWidth: 130,
          headerName: 'Active',
          field: 'isActive',
        },
        {
          minWidth: 130,
          headerName: 'Internal',
          field: 'isCollinsInternal',
        },
        {
          minWidth: 130,
          headerName: 'Federated',
          field: 'isFederated',
        },
        {
          minWidth: 130,
          headerName: 'Invitation',
          field: 'sendInvite',
        },
        {
          minWidth: 130,
          headerName: 'Airlines',
          field: 'airlineIds',
          valueGetter: params => {
            if (params.data.airlineIds.length) {
              const airlineNames = this.airlines?.filter(item =>
                params.data.airlineIds.includes(item.id),
              )
              return airlineNames.length > 0
                ? airlineNames.map(item => item.name).join(', ')
                : ''
            }
            return ''
          },
        },

        {
          minWidth: 130,
          headerName: 'Roles',
          field: 'groupIds',
          valueGetter: params => {
            if (params.data.groupIds.length) {
              const groupNames = this.groups?.filter(item =>
                params.data.groupIds.includes(item.id),
              )
              return groupNames.length > 0
                ? groupNames.map(item => item.name).join(', ')
                : ''
            }
            return ''
          },
        },
        {
          minWidth: 130,
          headerName: 'Report Types',
          field: 'acmfReportTypeIds',
          valueGetter: params => {
            if (params.data.acmfReportTypeIds.length) {
              const reportNames = this.reportTypes?.filter(item =>
                params.data.acmfReportTypeIds.includes(item.id),
              )
              return reportNames.length > 0
                ? reportNames.map(item => item.displayName).join(', ')
                : ''
            }
            return ''
          },
        },
        {
          minWidth: 130,
          headerName: 'Legacy Roles',
          field: 'roleIds',
          valueGetter: params => {
            if (params.data.roleIds.length) {
              const roleNames = this.roles?.filter(item =>
                params.data.roleIds.includes(item.id),
              )
              return roleNames.length > 0
                ? roleNames.map(item => item.name).join(', ')
                : ''
            }
            return ''
          },
        },
      ]
    },

    getGridClasses() {
      return [
        `asc-ag-grid-filter-${this._uid}--container`,
        'asc-theme-icons',
        'multiCheckboxSelectGrid',
      ]
    },
  },

  watch: {
    showDialog: {
      handler(newVal) {
        if (!newVal) {
          this.clearRowData()
        }
      },
    },
  },

  methods: {
    importFile(file) {
      if (file) {
        const fileByteArray = []
        const reader = new FileReader()
        reader.readAsArrayBuffer(file)
        reader.onloadend = evt => {
          if (evt.target.readyState === FileReader.DONE) {
            const array = new Uint8Array(evt.target.result)
            for (const a of array) {
              fileByteArray.push(a)
            }
            const arr = []
            for (let i = 0; i !== fileByteArray.length; ++i) {
              arr[i] = String.fromCharCode(fileByteArray[i])
            }
            const bstr = arr.join('')

            const workbook = read(bstr, {
              type: 'binary',
            })
            this.populateGrid(workbook)
          }
        }
      }
    },

    populateGrid(workbook) {
      const firstSheetName = workbook.SheetNames[0]
      const worksheet = workbook.Sheets[firstSheetName]
      const workbookJson = utils.sheet_to_json(worksheet, {
        defval: '',
        raw: false,
      })

      if (workbookJson.length + this.getRowCount > this.maxRows) {
        workbookJson.length = this.maxRows - this.getRowCount
      }

      const newRows = []

      for (let x = 0; x < workbookJson.length; x++) {
        const user = {}
        user.roleIds = this.validateRoles(workbookJson[x].roleIds)
        user.groupIds = this.validateGroups(workbookJson[x].groupIds)
        user.acmfReportTypeIds = this.validateReportTypes(
          workbookJson[x].acmfReportTypeIds,
        )
        user.airlineIds = this.validateAirlines(workbookJson[x].airlineIds)
        user.country = this.validateCountry(workbookJson[x].country)
        user.company = this.validateCompany(workbookJson[x].company)

        user.signInName = workbookJson[x].userName || ''
        user.firstName = workbookJson[x].firstName || ''
        user.lastName = workbookJson[x].lastName || ''
        user.userEmail = workbookJson[x].userEmail || ''

        user.isUSPerson = workbookJson[x].isUSPerson?.toLowerCase() === 'true'
        user.isActive = true
        user.isCollinsInternal =
          workbookJson[x].isCollinsInternal?.toLowerCase() === 'true'
        user.isFederated = workbookJson[x].isFederated?.toLowerCase() === 'true'
        user.sendInvite = workbookJson[x].sendInvite?.toLowerCase() === 'true'

        user.id = uuidv4()

        newRows.push(user)
      }
      this.agGridOptions.api.applyTransaction({ add: newRows })
      this.rowsValid = this.validateRows
      this.rowCount = this.getRowCount
    },

    validateCountry(country) {
      if (country) {
        return COUNTRIES.includes(country) ? country : ''
      }
      return ''
    },

    validateCompany(company) {
      if (company) {
        const validCompany = this.companies.find(item => item.name === company)
        return validCompany ? validCompany.name : null
      }
      return ''
    },

    validateGroups(groups) {
      if (groups) {
        const groupArray = groups.split(',').map(item => parseInt(item.trim()))
        const filteredGroups = this.groups
          .map(item => item.id)
          .filter(item => groupArray.includes(item))
        return groupArray.length === filteredGroups.length ? groupArray : []
      }
      return []
    },

    validateReportTypes(reports) {
      if (reports) {
        const reportArray = reports
          .split(',')
          .map(item => parseInt(item.trim()))
        const filteredReports = this.reportTypes
          .map(item => item.id)
          .filter(item => reportArray.includes(item))
        return reportArray.length === filteredReports.length ? reportArray : []
      }
      return []
    },

    validateAirlines(airlines) {
      if (airlines) {
        const airlineArray = airlines
          .split(',')
          .map(item => parseInt(item.trim()))
        const filteredAirlines = this.airlines
          .map(item => item.id)
          .filter(item => airlineArray.includes(item))
        return filteredAirlines.length === airlineArray.length
          ? airlineArray
          : []
      }
      return []
    },

    validateRoles(roles) {
      if (roles) {
        const roleArray = roles.split(',').map(item => parseInt(item.trim()))
        const filteredRoles = this.roles
          .map(item => item.id)
          .filter(item => roleArray.includes(item))
        return filteredRoles.length === roleArray.length ? roleArray : []
      }
      return []
    },

    onCloseSmallDialog() {
      this.showSmallDialog = false
      this.editingUser = false
      this.duplicateUser = false
      this.addingUser = false
      this.usersToAdd = 1
      this.addNewCompany = false
      this.clearSelections()
    },

    editUserClicked() {
      if (this.selections.length === 1) {
        this.currentUser = {
          ...this.selections[0].data,
        }
      }
      this.showSmallDialog = true
      this.editingUser = true
    },

    duplicateUserClicked() {
      if (this.selections.length === 1) {
        this.currentUser = {
          ...this.selections[0].data,
          firstName: '',
          lastName: '',
          signInName: '',
          userEmail: '',
          id: null,
        }
        this.duplicateUser = true
        this.showSmallDialog = true
      }
    },

    deleteUserClicked() {
      const selectedData = this.agGridOptions.api.getSelectedRows()
      this.agGridOptions.api.applyTransaction({ remove: selectedData })
      this.rowCount = this.getRowCount
      this.rowsValid = this.validateRows
    },

    addManualUserClicked() {
      this.addingUser = true
      this.showSmallDialog = true
    },

    usersToAddChanged(model) {
      if (model !== 1) {
        this.currentUser.firstName = ''
        this.currentUser.lastName = ''
        this.currentUser.signInName = ''
        this.currentUser.userEmail = ''
      }
    },

    submitSmallDialogClicked() {
      if (this.addingUser) {
        for (let x = 0; x < this.usersToAdd; x++) {
          const newUser = {
            ...this.currentUser,
            id: uuidv4(),
          }
          this.agGridOptions.api.applyTransaction({
            add: [newUser],
            addIndex: 0,
          })
        }
      } else if (this.selections.length <= 1) {
        if (this.currentUser.id === null) {
          const newUser = {
            ...this.currentUser,
            id: uuidv4(),
          }
          this.agGridOptions.api.applyTransaction({
            add: [newUser],
            addIndex: 0,
          })
        } else {
          const newUser = {
            ...this.currentUser,
          }
          this.agGridOptions.api.applyTransaction({ update: [newUser] })
        }
      } else if (this.selections.length > 0) {
        const editedFields = {}
        for (const item in this.currentUser) {
          if (this.currentUser[item] && this.currentUser[item].length > 0) {
            editedFields[item] = this.currentUser[item]
          } else if (
            this.currentUser[item] &&
            (item === 'isActive' ||
              item === 'isCollinsInternal' ||
              item === 'isFederated' ||
              item === 'isUSPerson' ||
              item === 'sendInvite')
          ) {
            editedFields[item] = this.currentUser[item]
          }
        }
        const itemsToUpdate = this.agGridOptions.api
          .getSelectedRows()
          .map(item => {
            const currentRowNode = this.agGridOptions.api.getRowNode(
              item.id,
            ).data
            return {
              ...currentRowNode,
              ...editedFields,
            }
          })
        this.agGridOptions.api.applyTransaction({ update: itemsToUpdate })
      }
      if (this.addNewCompany) {
        this.newCompanies.push(this.currentUser.company)
      }
      this.rowsValid = this.validateRows
      this.rowCount = this.getRowCount
      this.showSmallDialog = false
      this.editingUser = false
      this.duplicateUser = false
      this.addingUser = false
      this.usersToAdd = 1
      this.addNewCompany = false
      this.clearSelections()
    },

    clearSelections() {
      this.currentUser = {
        firstName: '',
        lastName: '',
        signInName: '',
        userEmail: '',
        company: null,
        country: '',
        isUSPerson: false,
        isActive: true,
        isCollinsInternal: false,
        isFederated: false,
        sendInvite: false,
        airlineIds: [],
        acmfReportTypeIds: [],
        roleIds: [],
        groupIds: [],
        id: null,
      }
    },

    handleSelectionChanged(selectedRows) {
      this.selections = selectedRows
    },

    onSubmitDialog() {
      const rowData = []
      this.agGridOptions.api.forEachNode(rowNode => {
        rowData.push(rowNode.data)
      })
      this.$emit('onSubmit', rowData)
    },

    onCloseDialog() {
      this.onClose()
    },

    revertToDropdown() {
      this.addNewCompany = false
      this.currentUser.company = null
    },

    clearRowData() {
      this.agGridOptions.api.setRowData([])
      this.rowCount = 0
      this.rowsValid = false
      this.files = []
    },
  },
}
</script>

<style lang="scss">
.asc-bulk-user-mgmt-container {
  .asc-autocomplete-selector {
    width: 100%;
  }
}
</style>
